import { Accept } from 'react-dropzone';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import CustomToggle from '../../../../../../../shared/CustomToggle';
import ImageUpload from '../../../../../../../shared/ImageUpload';
import AssetFormField from './AssetFormField';
import { AssetFormData, AssetFormMode, AssetView, VinFields } from './types';

interface AssetGeneralInfoProps {
  mode: AssetFormMode;
  register: UseFormRegister<AssetFormData>;
  control: Control<AssetFormData>;
  errors: FieldErrors<AssetFormData>;
  accept: Accept;
  assetImage: File | null;
  onDrop: (files: File[]) => void;
  /** Edit only: existing image URL (renders preview + remove instead of dropzone). */
  currentImageUrl?: string;
  onRemoveImage?: () => void;
  /** Create only: decoded VIN readout. */
  vinFields: VinFields;
  /** Edit only: source of the read-only asset-type/VIN/identity displays. */
  selectedAsset?: AssetView | null;
}

/**
 * "General Information" section: photo, display name, asset-type, VIN, and the
 * vehicle-identity readout. In CREATE these are editable (type toggle + VIN
 * input + decoded-VIN readout); in EDIT — matching the legacy EditAsset — the
 * asset type, VIN, and make/model/year/class are read-only displays sourced
 * from the existing asset (type and VIN are immutable after creation).
 */
export default function AssetGeneralInfo({
  mode,
  register,
  control,
  errors,
  accept,
  assetImage,
  onDrop,
  currentImageUrl,
  onRemoveImage,
  vinFields,
  selectedAsset,
}: AssetGeneralInfoProps) {
  const isEdit = mode === 'edit';
  const assetType = selectedAsset?.asset_type ?? 'truck';

  return (
    <div>
      <h2 className="text-[18px] font-[600] text-primary">
        General Information
      </h2>
      <div className="flex w-full flex-col gap-[24px] md:flex-row">
        <div className="flex w-full flex-col items-start space-y-1 md:w-1/2">
          <p className="text-[14px] text-[#7D8191]">Photo</p>
          <div className="w-full max-w-[548px] border-[1px] border-[#EAECF0] bg-[#808080] bg-opacity-[8%] p-3">
            <ImageUpload
              onDrop={onDrop}
              accept={accept}
              successMessage={
                assetImage !== null ? 'Document uploaded successfully!' : ''
              }
              uploadedFileNames={[assetImage?.name ?? '']}
              currentImageUrl={currentImageUrl}
              onRemoveImage={onRemoveImage}
            />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-y-4 md:w-1/2">
          <AssetFormField
            id="assetDisplayName"
            label="Asset Display Name"
            placeholder="Ex. Unit 3"
            error={errors.assetDisplayName?.message}
            registration={register('assetDisplayName', {
              required: 'Asset Display Name is required',
              maxLength: {
                value: 30,
                message: 'Maximum length is 30 characters',
              },
            })}
          />
          {isEdit ? (
            <div className="w-full space-y-1">
              <p className="text-[14px] text-[#7D8191]">Asset Type</p>
              <div className="flex flex-row items-center space-x-1 align-middle">
                <img alt={`${assetType}-icon`} src={`/${assetType}.svg`} />
                <p className="text-[16px] text-primary">
                  {assetType === 'truck' ? 'Truck' : 'Trailer'}
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full space-y-1">
              <p className="text-[14px] text-[#7D8191]">Asset Type</p>
              <Controller
                name="assetType"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CustomToggle
                    value={value}
                    onChange={onChange}
                    options={[
                      { icon: '/truck.svg', label: 'Truck', value: 'truck' },
                      {
                        icon: '/trailer.svg',
                        label: 'Trailer',
                        value: 'trailer',
                      },
                    ]}
                  />
                )}
              />
            </div>
          )}
          {isEdit ? (
            <div className="w-full space-y-1">
              <p className="text-[14px] text-[#7D8191]">VIN Number</p>
              <div className="flex flex-row items-center space-x-1 align-middle">
                <p className="text-[16px] text-primary">
                  {selectedAsset?.vehicle_id_number}
                </p>
              </div>
            </div>
          ) : (
            <AssetFormField
              id="vinNumber"
              label="VIN Number"
              placeholder="Ex. 4Y1SL65848Z411439"
              error={errors.vinNumber?.message}
              registration={register('vinNumber', {
                required: 'VIN Number is required',
                pattern: {
                  value: /^[A-Z0-9]{17}$/,
                  message:
                    'Invalid VIN Number, must be 17 uppercase letters or digits',
                },
              })}
            />
          )}
          {isEdit ? (
            <EditIdentityReadout selectedAsset={selectedAsset} />
          ) : (
            <CreateIdentityReadout vinFields={vinFields} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Decoded-VIN readout shown during create (from the live NHTSA lookup). */
function CreateIdentityReadout({ vinFields }: { vinFields: VinFields }) {
  return (
    <div className="flex flex-col items-start gap-y-1 align-middle">
      {vinFields?.class ? (
        <div className="flex flex-row items-center gap-x-1 align-middle">
          <p className="text-[12px] text-graysecondary">Class</p>
          <p className="text-[14px] text-primary">{vinFields?.class}</p>
        </div>
      ) : null}
      {vinFields?.make && vinFields?.make?.length > 0 && (
        <div className="flex flex-row items-center gap-x-1 align-middle">
          <p className="text-[12px] text-graysecondary">Make</p>
          <p className="text-[14px] text-primary">{vinFields?.make}</p>
        </div>
      )}
      {vinFields?.model && vinFields?.model?.length > 0 && (
        <div className="flex flex-row items-center gap-x-1 align-middle">
          <p className="text-[12px] text-graysecondary">Model</p>
          <p className="text-[14px] text-primary">{vinFields?.model}</p>
        </div>
      )}
      {vinFields?.year && vinFields?.year?.length > 0 && (
        <div className="flex flex-row items-center gap-x-1 align-middle">
          <p className="text-[12px] text-graysecondary">Year</p>
          <p className="text-[14px] text-primary">{vinFields?.year}</p>
        </div>
      )}
    </div>
  );
}

/** Read-only vehicle identity shown during edit (stored on the asset). */
function EditIdentityReadout({
  selectedAsset,
}: {
  selectedAsset?: AssetView | null;
}) {
  if (selectedAsset?.asset_type === 'truck') {
    return (
      <div className="flex flex-col items-start gap-y-1 align-middle">
        {selectedAsset?.truck_class && (
          <div className="flex flex-row items-center gap-x-1 align-middle">
            <p className="text-[12px] text-graysecondary">Class</p>
            <p className="text-[14px] text-primary">
              {selectedAsset?.truck_class +
                ' (' +
                selectedAsset?.truck_class_number +
                ')'}
            </p>
          </div>
        )}
        {selectedAsset?.truck_make && selectedAsset?.truck_make?.length > 0 && (
          <div className="flex flex-row items-center gap-x-1 align-middle">
            <p className="text-[12px] text-graysecondary">Make</p>
            <p className="text-[14px] text-primary">{selectedAsset?.truck_make}</p>
          </div>
        )}
        {selectedAsset?.truck_model &&
          selectedAsset?.truck_model?.length > 0 && (
            <div className="flex flex-row items-center gap-x-1 align-middle">
              <p className="text-[12px] text-graysecondary">Model</p>
              <p className="text-[14px] text-primary">
                {selectedAsset?.truck_model}
              </p>
            </div>
          )}
        {selectedAsset?.truck_year && selectedAsset?.truck_year > 0 && (
          <div className="flex flex-row items-center gap-x-1 align-middle">
            <p className="text-[12px] text-graysecondary">Year</p>
            <p className="text-[14px] text-primary">{selectedAsset?.truck_year}</p>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-y-1 align-middle">
      {selectedAsset?.trailer_make &&
        selectedAsset?.trailer_make?.length > 0 && (
          <div className="flex flex-row items-center gap-x-1 align-middle">
            <p className="text-[12px] text-graysecondary">Make</p>
            <p className="text-[14px] text-primary">
              {selectedAsset?.trailer_make}
            </p>
          </div>
        )}
      {selectedAsset?.trailer_model &&
        selectedAsset?.trailer_model?.length > 0 && (
          <div className="flex flex-row items-center gap-x-1 align-middle">
            <p className="text-[12px] text-graysecondary">Model</p>
            <p className="text-[14px] text-primary">
              {selectedAsset?.trailer_model}
            </p>
          </div>
        )}
      {selectedAsset?.trailer_year && selectedAsset?.trailer_year > 0 && (
        <div className="flex flex-row items-center gap-x-1 align-middle">
          <p className="text-[12px] text-graysecondary">Year</p>
          <p className="text-[14px] text-primary">
            {selectedAsset?.trailer_year}
          </p>
        </div>
      )}
    </div>
  );
}
